/**
 * Gestione stato "sblocco" delle regioni.
 *
 * Model:
 *  - Al primo avvio l'utente sceglie UNA regione gratuita (permanente).
 *  - Puo' comprare il bundle "tutte le regioni" (IAP non-consumable €7.99).
 *  - Stato persistito in localStorage (la verifica receipt IAP la fa
 *    RevenueCat sul Capacitor build; in web/dev questo modulo simula).
 *
 * Storage shape:
 *   {
 *     freeRegion: "Lombardia" | null,  // null = onboarding non completato
 *     purchasedAll: boolean,
 *     purchasedAt?: string             // ISO timestamp, solo se purchasedAll
 *   }
 */

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "itinere:unlock:v1";

export interface UnlockState {
  freeRegion: string | null;
  purchasedAll: boolean;
  purchasedAt?: string;
}

const DEFAULT_STATE: UnlockState = {
  freeRegion: null,
  purchasedAll: false,
};

/**
 * Lettura diretta dello stato dal localStorage, senza subscription React.
 *
 * Usato in componenti dove l'iscrizione reattiva (useUnlock) causerebbe
 * troppi re-render. Es. PopupContent dentro 1200+ Marker: con useUnlock
 * ogni cambio stato triggerebbe 1200 re-render simultanei → freeze
 * WKWebView. Con readUnlockState() la lettura e' fresh al momento del
 * render, ma niente subscription = niente re-render automatico.
 */
export function readUnlockState(): UnlockState {
  return readStorage();
}

function readStorage(): UnlockState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    return {
      freeRegion: typeof parsed.freeRegion === "string" ? parsed.freeRegion : null,
      purchasedAll: !!parsed.purchasedAll,
      purchasedAt: typeof parsed.purchasedAt === "string" ? parsed.purchasedAt : undefined,
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function writeStorage(state: UnlockState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    // Notifica altri componenti che usano l'hook
    window.dispatchEvent(new CustomEvent("itinere:unlock-changed"));
  } catch {
    // ignora (storage pieno, modalita' incognito, ecc.)
  }
}

/**
 * Hook reattivo per leggere/aggiornare lo stato di sblocco.
 *
 * Componenti che lo usano si re-renderizzano quando un altro componente
 * cambia lo stato (via custom event 'itinere:unlock-changed').
 */
export function useUnlock() {
  const [state, setState] = useState<UnlockState>(readStorage);

  useEffect(() => {
    const handler = () => setState(readStorage());
    window.addEventListener("itinere:unlock-changed", handler as EventListener);
    // Sincronizza anche fra tab diverse (storage event)
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("itinere:unlock-changed", handler as EventListener);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const setFreeRegion = useCallback((region: string) => {
    writeStorage({ ...readStorage(), freeRegion: region });
  }, []);

  /**
   * Marca "tutte le regioni acquistate". In dev/web e' una simulazione;
   * sul build Capacitor verra' chiamata dopo che RevenueCat conferma la
   * transazione IAP.
   */
  const markPurchasedAll = useCallback(() => {
    writeStorage({
      ...readStorage(),
      purchasedAll: true,
      purchasedAt: new Date().toISOString(),
    });
  }, []);

  /** Restore purchase: utile in app store flow. Per ora stub. */
  const restorePurchases = useCallback(async () => {
    // In Capacitor: chiama RevenueCat restorePurchases() e aggiorna stato.
    // In web/dev: noop.
    return readStorage();
  }, []);

  /** Reset stato (utile per test in dev) */
  const resetForDev = useCallback(() => {
    writeStorage(DEFAULT_STATE);
  }, []);

  return { state, setFreeRegion, markPurchasedAll, restorePurchases, resetForDev };
}

/**
 * Helper puro (no hook) per sapere se un POI e' sbloccato.
 * Usabile sia dentro componenti React (passando lo stato) sia in pure logic
 * (callbacks Leaflet, filter di markers, ecc.).
 */
export function isPoiUnlocked(
  poiRegion: string | undefined,
  state: UnlockState,
): boolean {
  if (state.purchasedAll) return true;
  if (!state.freeRegion) return false;
  if (!poiRegion) return false;
  return poiRegion.toLowerCase() === state.freeRegion.toLowerCase();
}
